import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tooltip, Button, Select } from "antd";
import { ReloadOutlined, ArrowRightOutlined } from "@ant-design/icons";
import {
  TbCurrencyDollar,
  TbShoppingCart,
  TbReceipt,
  TbAlertTriangle,
  TbCreditCard,
  TbBuildingStore,
  TbTrendingUp,
  TbDiscount,
  TbClock,
  TbAlertCircle,
} from "react-icons/tb";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import dayjs from "dayjs";
import { useRouterStore } from "@stores";
import { SubscriptionStatusEnum } from "@enums";
import "../../css/operator.css";

type DashPeriod = "today" | "week";

const COLORS = ["#08000a", "#E8320A", "#534AB7", "#854F0B", "#0C447C"];

const PERIOD_OPTIONS: { value: DashPeriod; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "week", label: "Esta semana" },
];

// ── skeleton ──────────────────────────────────────────────
const Skeleton = ({ w = "100%", h = 20 }: { w?: string; h?: number }) => (
  <div className="do-skeleton" style={{ width: w, height: h }} />
);

// ── KPI card ─────────────────────────────────────────────
const KpiCard = ({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  color: string;
}) => (
  <div className="do-kpi-card">
    <div className="do-kpi-icon" style={{ background: `${color}18`, color }}>
      {icon}
    </div>
    <div className="do-kpi-info">
      <span className="do-kpi-value">{value}</span>
      <span className="do-kpi-label">{label}</span>
      {sub && <span className="do-kpi-sub">{sub}</span>}
    </div>
  </div>
);

const DashboardOperatorComponent = () => {
  const navigate = useNavigate();
  const subscriptionStatusCode = useRouterStore(
    (s) => s.subscriptionStatusCode,
  );
  const daysLeft = useRouterStore((s) => s.daysLeft);
  const [period, setPeriod] = useState<DashPeriod>("today");

  const fmt = (n?: number) =>
    `$${(n ?? 0).toLocaleString("es-EC", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const fmtNum = (n?: number) => (n ?? 0).toLocaleString("es-EC");

  return (
    <div className="do-page">
      {subscriptionStatusCode === SubscriptionStatusEnum.ABOUT_TO_EXPIRE && (
        <div
          className={`do-sub-banner ${daysLeft !== null && daysLeft <= 2 ? "danger" : "warning"}`}
        >
          <div className="do-sub-banner-icon">
            {daysLeft !== null && daysLeft <= 2 ? (
              <TbAlertCircle size={18} />
            ) : (
              <TbClock size={18} />
            )}
          </div>
          <div className="do-sub-banner-text">
            {daysLeft === 0 ? (
              <>
                <strong>Tu suscripción vence hoy.</strong> Contacta a tu
                administrador para renovar y evitar perder el acceso.
              </>
            ) : daysLeft === 1 ? (
              <>
                <strong>Tu suscripción vence mañana.</strong> Contacta a tu
                administrador para renovar.
              </>
            ) : (
              <>
                <strong>Tu suscripción vence en {daysLeft} días.</strong>{" "}
                Comunícate con tu administrador para renovar a tiempo.
              </>
            )}
          </div>
          <div className="do-sub-banner-days">
            {daysLeft !== null && daysLeft >= 0 && (
              <span
                className={`do-sub-days-badge ${daysLeft <= 2 ? "danger" : "warning"}`}
              >
                {daysLeft === 0 ? "Vence hoy" : `${daysLeft}d restantes`}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOperatorComponent;
